/**
 * 💨 Smoke — the first particle system this game has had, second attempt.
 *
 * **T128 · `Development/Minecraft/15_Animated_Textures_And_The_Flame.md` §5**
 *
 * 🎯 **haugur asked for it twice** — *"a little smoke"* on the campfire, and
 * *"tiny smoke as in minecraft"* on the torch.
 *
 * ## 🔴 The first attempt STOPPED THE WORLD DRAWING, and why matters more than what
 *
 * 🎯 **haugur, 2026-09-01:** *"it looks like staging refuses to draw the world
 * after 1 draw"*, and then the sentence that identified it: *"as soon as i place
 * a log on the fire on dev, the world stops moving."*
 *
 * 🔴 **A material's shader is not compiled until something using it is DRAWN.**
 * So a bad smoke shader was invisible in a world with no fire near the camera —
 * it passed the gates, 305 client tests, and **two headless boots that reported
 * zero console errors**, because `shoot.mjs` spawns where nothing is burning.
 * **It waited for a player to light a fire.**
 *
 * ✅ **Two things are done differently here, and both are the point:**
 *
 * | | |
 * |---|---|
 * | 🔴 **The transform is three's own {@link billboarding}** | the first attempt hand-rolled a `vertexNode`, and that is what broke. **A camera-facing quad is a solved problem in the library we already depend on** |
 * | 🔴 **The material is COMPILED AT STARTUP** — {@link primeSmoke} | so a fault appears on every boot, in front of `shoot.mjs`, **instead of waiting in ambush for the first fire** |
 *
 * ## 🔴 Stateless: a particle's position is a pure function of time
 *
 * **Nothing is simulated and nothing is updated per frame.** Each puff carries
 * where it was born and a phase; the shader works out the rest from
 * `time + phase`. **The CPU touches a particle once, when its chunk is built.**
 *
 * ⚠️ **That is a deliberate answer to the frame-rate fault of 2026-08-31.** A
 * per-frame CPU particle update is thousands of positions rewritten sixty times
 * a second, and **rain will want thousands where smoke wants tens.**
 *
 * ## ⬜ What this version does NOT do
 *
 * **A puff does not GROW as it rises.** {@link billboarding} sizes its quad from
 * the geometry's own corners, so an animated size would mean overriding
 * `positionLocal` as well — **and composing my own node with three's is exactly
 * the class of thing that broke last time.** ✅ **Size varies between puffs
 * instead of within one**, which reads nearly as well and risks nothing. 👉 Worth
 * revisiting once this pipeline has been trusted for a while.
 */

import {
  BufferAttribute,
  BufferGeometry,
  DataTexture,
  DoubleSide,
  LinearFilter,
  Mesh,
  NormalBlending,
  RGBAFormat,
  type Scene,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { attribute, billboarding, float, texture, uniform, uv, vec3 } from 'three/tsl';

/** Seconds one puff takes to rise, spread and fade. */
const HEARTH_LIFETIME = 4.2;
const TORCH_LIFETIME = 2.6;

/**
 * How far a puff climbs before it is gone, in metres.
 *
 * ⚠️ **Minecraft's campfire smoke rises about 10 blocks** (doc 15 §5) and this is
 * deliberately far short of it. 🎯 **haugur asked for *a little* smoke on the
 * hearth and *tiny* on the torch**, and a ten-metre column over every hearth
 * would be the loudest thing in Iceland. 🔴 **Playtest numbers, all of them.**
 */
const HEARTH_RISE = 3.4;
const TORCH_RISE = 1.1;

/** How many puffs each fire keeps in the air at once. */
const HEARTH_PUFFS = 7;
const TORCH_PUFFS = 3;

/** How wide a puff is, in metres — smallest to largest across a fire's puffs. */
const HEARTH_SIZE = [0.3, 0.8] as const;
const TORCH_SIZE = [0.09, 0.22] as const;

/** How far a puff wanders sideways over its life, in metres. */
const DRIFT = 0.55;

/** How solid the smoke is at its densest. */
const HEARTH_STRENGTH = 0.5;
const TORCH_STRENGTH = 0.32;

/**
 * 💨 **The puff, drawn rather than photographed.**
 *
 * ⚠️ **Procedural, so it costs nothing and needs no sign-off.** The block
 * textures are photographs and the flame is painted frames; a puff of smoke is a
 * soft round blob, and asking an image model for one would be spending money and
 * haugur's attention on a radial gradient.
 *
 * 🔴 **The alpha does the work, not the colour.** Smoke is grey everywhere; what
 * makes it read as smoke is a soft edge, so the falloff is squared to keep the
 * middle solid and the rim faint.
 */
export function buildSmokeTexture(size = 32): DataTexture {
  const data = new Uint8Array(size * size * 4);
  const middle = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - middle) / middle;
      const dy = (y - middle) / middle;
      const falloff = Math.max(0, 1 - Math.hypot(dx, dy));
      const i = (y * size + x) * 4;
      /* eslint-disable security/detect-object-injection --
         Loop counters into an array this function just allocated. Same reasoning
         and same wording as `textures.ts` and `flame.ts`. */
      data[i] = 226;
      data[i + 1] = 224;
      data[i + 2] = 220;
      data[i + 3] = Math.round(falloff * falloff * 255);
      /* eslint-enable security/detect-object-injection */
    }
  }
  const tex = new DataTexture(data, size, size, RGBAFormat);
  /* Linear both ways: a puff is soft by nature, and the nearest-neighbour look
     the block tiles want would make it a pixellated disc. */
  tex.magFilter = LinearFilter;
  tex.minFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** The clock every puff is driven from, in seconds. Set once per frame. */
export const smokeTime = uniform(0);

/**
 * The material every puff shares.
 *
 * 🔴 **`billboarding` is three's own, and using it is the whole lesson of the
 * first attempt.** It returns a clip-space vertex position for a quad that faces
 * the camera, and it is written and maintained by the library. ⚠️ **The hand-
 * rolled `vertexNode` it replaces is what stopped the world drawing.**
 *
 * ⚠️ **`position` is where the puff IS, in scene space** — `billboarding` puts it
 * into the model matrix's translation, so the geometry's own corners are left to
 * carry the quad's size and nothing else.
 *
 * 🔴 **NORMAL blending, unlike the flame's additive.** A flame is light and adds
 * to what is behind it; **smoke is a thing in the way** and has to dull what it
 * covers. Adding it would make a column of smoke *brighten* the night sky.
 */
export function buildSmokeMaterial(tex: DataTexture): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();

  /* 🔴 `age` runs 0…1 over a puff's life, offset by its own phase so a fire's
     puffs do not pulse in unison. `.add`, `.mod` and `.div` only — the same
     small vocabulary `flame.ts` already proves on this renderer. */
  const life = attribute('life', 'float');
  const age = smokeTime.add(attribute('phase', 'float')).mod(life).div(life);

  /* Where it is: born here, drifting sideways and climbing, both with age. */
  const centre = attribute('origin', 'vec3')
    .add(attribute('drift', 'vec3').mul(age))
    .add(vec3(0, attribute('rise', 'float').mul(age), 0));

  material.vertexNode = billboarding({ position: centre });

  const tile = texture(tex, uv());
  material.colorNode = vec3(tile.r, tile.g, tile.b);

  /* ⚠️ **Fades in as well as out.** A fire's smoke is a loop, so the moment a
     puff dies is the moment it is reborn — both ends have to be invisible or the
     seam shows. `age·(1−age)·4` is 0 at both ends and 1 in the middle. */
  const fade = age.mul(float(1).sub(age)).mul(float(4));
  material.opacityNode = tile.a.mul(fade).mul(attribute('strength', 'float'));

  material.transparent = true;
  /* ⚠️ Puffs overlap constantly and are translucent, so writing depth would make
     each cut a hole in the ones behind it. They still depth-TEST, so smoke goes
     behind a wall properly. */
  material.depthWrite = false;
  material.blending = NormalBlending;
  material.side = DoubleSide;
  /* Anything this faint is not smoke, and dropping it early keeps the fill rate
     down where a farm has several fires. Same number and same reason as the
     flame's. */
  material.alphaTest = 0.02;

  return material;
}

/** One fire making smoke, in the chunk's own SCENE coordinates. */
export interface SmokeSource {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly kind: 'hearth' | 'torch';
}

/* A tiny deterministic hash, so a puff's drift, size and phase are stable across
   rebuilds. ⚠️ NOT `Math.random`: a chunk re-meshes whenever a neighbour changes,
   and a puff that picked a new direction each time would read as a gust that is
   not there. Same rule `berries.ts` already follows for where a berry grows. */
function hash(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/* eslint-disable security/detect-object-injection --
   Numeric work over typed arrays: every index is a loop counter bounded above,
   writing into an array this function just allocated. Same reasoning and same
   wording as `flame.ts` and `textures.ts`. */

/**
 * 💨 **Every fire's smoke in one chunk, as one geometry.**
 *
 * @param sources the fires, in the coordinates the finished mesh will sit in —
 *   **scene space, not world space.** ⚠️ `billboarding` writes the puff's own
 *   position into the model matrix, so the mesh itself sits at the origin and
 *   these have to be where the puff actually is. The caller adds the chunk's
 *   scene position, which is already `origin − renderOrigin`.
 * @returns `null` when nothing is burning — the ordinary case, and it must cost
 *   no allocation at all.
 */
export function buildSmokeGeometry(sources: readonly SmokeSource[]): BufferGeometry | null {
  if (sources.length === 0) return null;

  const puffs = sources.reduce(
    (sum, s) => sum + (s.kind === 'hearth' ? HEARTH_PUFFS : TORCH_PUFFS),
    0,
  );
  if (puffs === 0) return null;

  const verts = puffs * 4;
  const pos = new Float32Array(verts * 3);
  const uvs = new Float32Array(verts * 2);
  const origin = new Float32Array(verts * 3);
  const drift = new Float32Array(verts * 3);
  const phase = new Float32Array(verts);
  const life = new Float32Array(verts);
  const rise = new Float32Array(verts);
  const strength = new Float32Array(verts);
  const indices = new Uint32Array(puffs * 6);

  /* The quad's corners, scaled to the puff's own size. 🔴 `billboarding` takes
     the SIZE from here — see the note at the top about what this version does
     not do. */
  const CORNERS: readonly (readonly [number, number, number, number])[] = [
    [-0.5, -0.5, 0, 1],
    [0.5, -0.5, 1, 1],
    [0.5, 0.5, 1, 0],
    [-0.5, 0.5, 0, 0],
  ];

  let p = 0;
  for (const [s, source] of sources.entries()) {
    const hearth = source.kind === 'hearth';
    const count = hearth ? HEARTH_PUFFS : TORCH_PUFFS;
    const [small, large] = hearth ? HEARTH_SIZE : TORCH_SIZE;

    for (let k = 0; k < count; k++) {
      const seed = s * 977 + k * 31;
      const a = hash(seed) * Math.PI * 2;
      const spread = hash(seed + 1) * DRIFT * (hearth ? 1 : 0.4);
      const lifetime = (hearth ? HEARTH_LIFETIME : TORCH_LIFETIME) * (0.85 + hash(seed + 2) * 0.3);
      const size = small + (large - small) * hash(seed + 3);
      const base = p * 4;

      for (const [c, corner] of CORNERS.entries()) {
        const v = base + c;
        pos[v * 3] = corner[0] * size;
        pos[v * 3 + 1] = corner[1] * size;
        pos[v * 3 + 2] = 0;
        uvs[v * 2] = corner[2];
        uvs[v * 2 + 1] = corner[3];

        origin[v * 3] = source.x + 0.5;
        /* Started a little above the fire rather than inside it. */
        origin[v * 3 + 1] = source.y + (hearth ? 0.3 : 0.7);
        origin[v * 3 + 2] = source.z + 0.5;

        drift[v * 3] = Math.cos(a) * spread;
        drift[v * 3 + 1] = 0;
        drift[v * 3 + 2] = Math.sin(a) * spread;

        /* 🔴 Spread evenly through the lifetime, so a fire lit a moment ago has
           a column of smoke rather than one puff every four seconds. */
        phase[v] = (k / count) * lifetime;
        life[v] = lifetime;
        rise[v] = hearth ? HEARTH_RISE : TORCH_RISE;
        strength[v] = hearth ? HEARTH_STRENGTH : TORCH_STRENGTH;
      }

      const q = p * 6;
      indices[q] = base;
      indices[q + 1] = base + 1;
      indices[q + 2] = base + 2;
      indices[q + 3] = base;
      indices[q + 4] = base + 2;
      indices[q + 5] = base + 3;
      p++;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(pos, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setAttribute('origin', new BufferAttribute(origin, 3));
  geometry.setAttribute('drift', new BufferAttribute(drift, 3));
  geometry.setAttribute('phase', new BufferAttribute(phase, 1));
  geometry.setAttribute('life', new BufferAttribute(life, 1));
  geometry.setAttribute('rise', new BufferAttribute(rise, 1));
  geometry.setAttribute('strength', new BufferAttribute(strength, 1));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}
/* eslint-enable security/detect-object-injection */

/** A chunk's smoke as one object, or `null` if nothing there is burning. */
export function buildSmokeMesh(
  sources: readonly SmokeSource[],
  material: MeshBasicNodeMaterial,
): Mesh | null {
  const geometry = buildSmokeGeometry(sources);
  if (geometry === null) return null;
  const mesh = new Mesh(geometry, material);
  /* 🔴 Drawn after the flames, which are drawn after the world. Smoke is
     translucent and writes no depth, so it has to come last of the three. */
  mesh.renderOrder = 2;
  /* ⚠️ The shader moves every vertex a long way from where the geometry says it
     is, so three cannot work out a bounding box that means anything. Culling by
     that box would make a chunk's smoke vanish at the wrong moment. */
  mesh.frustumCulled = false;
  return mesh;
}

/**
 * 🔴 **Draw one invisible puff for ever, so the shader is COMPILED AT STARTUP.**
 *
 * 🎯 **This exists because of haugur's 2026-09-01:** *"as soon as i place a log
 * on the fire on dev, the world stops moving."*
 *
 * **A material's shader is not compiled until something using it is drawn.** The
 * first version of this file shipped a broken one, and **nothing could see it**:
 * the gates passed, 305 client tests passed, and `shoot.mjs` booted the real
 * client twice and reported **zero console errors** — because it spawns where
 * nothing is burning. The fault sat waiting for a player to light a fire.
 *
 * ✅ **One puff, strength 0, parked at the scene origin.** It costs a single quad
 * that is discarded by `alphaTest` before it shades a pixel, and it buys the one
 * thing that was missing: **if this shader is wrong, the world fails to draw on
 * EVERY boot, in front of every instrument, rather than in front of haugur.**
 *
 * ⚠️ **Do not "optimise" this away.** It looks like a mesh that does nothing, and
 * that is exactly what it is for.
 */
export function primeSmoke(scene: Scene, material: MeshBasicNodeMaterial): Mesh | null {
  const mesh = buildSmokeMesh([{ x: 0, y: 0, z: 0, kind: 'torch' }], material);
  if (mesh === null) return null;
  const strength = mesh.geometry.getAttribute('strength');
  for (let i = 0; i < strength.count; i++) strength.setX(i, 0);
  strength.needsUpdate = true;
  mesh.renderOrder = 2;
  scene.add(mesh);
  return mesh;
}
